import type { CallDetail, CallLegsResponse } from '../types';
import { strings } from '../strings/en';

export type OutcomeWord =
  | typeof strings.outcome.answered
  | typeof strings.outcome.missed
  | typeof strings.outcome.noAnswer
  | typeof strings.outcome.unknown;

export interface OutcomeInput {
  direction: string;
  /** 'answered' | 'missed' | 'abandoned' | 'unknown' | null */
  outcome: string | null | undefined;
}

/** One word a person understands. Unknown never becomes Answered (spec §6). */
export function deriveOutcome({ direction, outcome }: OutcomeInput): OutcomeWord {
  const o = outcome ?? 'unknown';
  if (direction === 'outbound') {
    // One vocabulary (owner send-back F3): the outcome word is always
    // Answered, never Connected -- "answered vs connected" read as two
    // different things to a non-technical user.
    if (o === 'answered') return strings.outcome.answered;
    if (o === 'missed' || o === 'abandoned') return strings.outcome.noAnswer;
    return strings.outcome.unknown;
  }
  if (o === 'answered') return strings.outcome.answered;
  if (o === 'missed' || o === 'abandoned') return strings.outcome.missed;
  return strings.outcome.unknown;
}

export function outcomeTone(word: OutcomeWord): 'neutral' | 'negative' | 'warning' {
  if (word === strings.outcome.missed || word === strings.outcome.noAnswer) return 'negative';
  if (word === strings.outcome.unknown) return 'warning';
  return 'neutral';
}

/**
 * One quality word for a person (spec §6). Anything unrecognised is Unknown;
 * Unknown is never rendered as Healthy.
 */
export function qualityWord(status: 'healthy' | 'degraded' | 'poor' | 'unknown' | string | null | undefined): string {
  if (status === 'healthy') return strings.quality.healthy;
  if (status === 'degraded') return strings.quality.degraded;
  if (status === 'poor') return strings.quality.poor;
  return strings.quality.unknown;
}

/**
 * Turn a call plus its legs into the input `deriveOutcome` wants. Extracted so
 * Interaction Detail does not inline this reasoning (it is the only place that
 * has legs, and the rule is easy to get subtly wrong).
 */
export function outcomeInputForDetail(
  call: Pick<CallDetail, 'direction' | 'duration_seconds' | 'outcome'>,
  legs: CallLegsResponse | null,
): OutcomeInput {
  if (call.outcome && call.outcome !== 'unknown') {
    return { direction: call.direction, outcome: call.outcome };
  }
  const ordered = legs ? [...legs.legs].sort((a, b) => a.leg_order - b.leg_order) : [];
  if (ordered.length === 0) {
    return { direction: call.direction, outcome: (call.duration_seconds ?? 0) > 0 ? 'answered' : 'unknown' };
  }
  const last = ordered[ordered.length - 1].outcome;
  if (last === 'answered') return { direction: call.direction, outcome: 'answered' };
  if (last === 'no_answer' || last === 'failed' || last === 'voicemail') {
    return { direction: call.direction, outcome: 'missed' };
  }
  if (last === 'transferred') {
    // A transfer is only a good outcome if a human actually picked up somewhere.
    const answeredSomewhere = ordered.some((l) => l.outcome === 'answered');
    return { direction: call.direction, outcome: answeredSomewhere ? 'answered' : 'unknown' };
  }
  return { direction: call.direction, outcome: 'unknown' };
}
